/**
 * Подписи детальных характеристик — как выводит монолит (`cian/business_shopping_centers/domain/enums.py`, `help`,
 * и словари `cian/business_shopping_centers/presenters.py`). Подписи `x-*` контракта во многом расходятся с монолитом
 */

/** `WEEK_DAYS_SHORT` */
export const WEEK_DAY_LABELS: Partial<Record<string, string>> = {
  workDays: 'Рабочие дни',
  weekend: 'Выходные',
  everyDay: 'Ежедневно',
  monday: 'Пн',
  tuesday: 'Вт',
  wednesday: 'Ср',
  thursday: 'Чт',
  friday: 'Пт',
  saturday: 'Сб',
  sunday: 'Вс',
};

/** `InputType` */
export const INPUT_TYPE_LABELS: Partial<Record<string, string>> = {
  separateFromTheYard: 'Отдельный со двора',
  separateFromTheStreet: 'Отдельный с улицы',
  commonFromTheYard: 'Общий со двора',
  commonFromTheStreet: 'Общий с улицы',
};

/** `LiftType` */
export const LIFT_TYPE_LABELS: Partial<Record<string, string>> = {
  cargo: 'Грузовой',
  telpher: 'Тельфер',
  passenger: 'Пассажирский',
  lift: 'Лифт',
  travelator: 'Траволатор',
  escalator: 'Эскалатор',
};

/** `CranageType` */
export const CRANAGE_TYPE_LABELS: Partial<Record<string, string>> = {
  overhead: 'Мостовой кран',
  beam: 'Кран-балка',
  railway: 'Ж/д кран',
  gantry: 'Козловой кран',
};

/** `PARKING_LOCATION_TYPE` */
export const PARKING_LOCATION_LABELS: Partial<Record<string, string>> = {
  internal: 'На территории объекта',
  external: 'За территорией объекта',
};

/** `PARKING_PURPOSE_TYPE` */
export const PARKING_PURPOSE_LABELS: Partial<Record<string, string>> = {
  cargo: 'для грузового транспорта',
  passenger: 'для легкового транспорта',
};

/** `GatesType` */
export const GATES_TYPE_LABELS: Partial<Record<string, string>> = {
  onRamp: 'На пандусе',
  dockType: 'Докового типа',
  atZero: 'На нулевой отметке',
};

/** `FloorType` */
export const FLOOR_TYPE_LABELS: Partial<Record<string, string>> = {
  polymeric: 'Полимерный',
  concrete: 'Бетон',
  linoleum: 'Линолеум',
  asphalt: 'Асфальт',
  tile: 'Плитка',
  selfLeveling: 'Наливной пол',
  reinforcedConcrete: 'Железобетон',
  wood: 'Дерево',
  laminate: 'Ламинат',
};

/** `TemperatureMode` */
export const TEMPERATURE_MODE_LABELS: Partial<Record<string, string>> = {
  freezing: 'Морозильный',
  cool: 'Холодный',
  warm: 'Теплый',
};

/** `AdditionalCharge`, в шаблоне — `lower` */
export const ADDITIONAL_CHARGE_LABELS: Partial<Record<string, string>> = {
  cleaning: 'уборка',
  communalPayments: 'коммунальные платежи',
  electricity: 'электричество',
  internet: 'интернет',
};

/**
 * `MATERIAL_TYPE`. У монолита подписи с `largeBlock` по `other` не находятся (ключи словаря без `.value`) — у нас есть.
 * `block`, `stalin`, `oldFond`, `boards` монолит не знает: подписи `x-*` контракта в нижнем регистре
 */
export const MATERIAL_TYPE_LABELS: Partial<Record<string, string>> = {
  reinforcedConcrete: 'железобетонное здание',
  brick: 'кирпичное здание',
  monolithicFrameWithBrickExternalWalls: 'монолитный каркас с наружными кирпичными стенами',
  panel: 'панельное здание',
  steel: 'стальная конструкция',
  wood: 'деревянное здание',
  largeBlock: 'крупноблочные',
  sandwichPanels: 'сэндвич-панели',
  metal: 'металлические',
  skeletonSheathed: 'каркасно-обшивные',
  other: 'из прочих материалов',
  block: 'блок',
  stalin: 'сталинский',
  oldFond: 'старый фонд',
  boards: 'щитовой',
};

/** `FLOOR_MATERIAL_TYPE` */
export const FLOOR_MATERIAL_TYPE_LABELS: Partial<Record<string, string>> = {
  reinforcedConcrete: 'железобетон',
  wood: 'дерево',
  steel: 'металл',
  hybrid: 'смешанные',
};

/** `VentilationType`, в шаблоне — `lower` */
export const VENTILATION_TYPE_LABELS: Partial<Record<string, string>> = {
  natural: 'естественная',
  forced: 'приточная',
  no: 'нет',
};

/** `ConditioningType`, в шаблоне — `lower` */
export const CONDITIONING_TYPE_LABELS: Partial<Record<string, string>> = {
  local: 'местное',
  central: 'центральное',
  no: 'нет',
};

/** `HeatingType`, в шаблоне — `lower`. Контракт отдаёт только эти значения */
export const HEATING_TYPE_LABELS: Partial<Record<string, string>> = {
  no: 'нет',
  central: 'центральное',
  autonomous: 'автономное',
};

/**
 * `ExtinguishingSystemType`, в шаблоне — `lower`. Ключи и новых значений контракта (`extinguishingSystemTypes`),
 * и устаревших (`extinguishingSystemType`) — подписи монолита по смыслу
 */
export const EXTINGUISHING_SYSTEM_LABELS: Partial<Record<string, string>> = {
  hydrantSystem: 'гидрантная',
  sprinklerSystem: 'спринклерная',
  powderSystem: 'порошковая',
  gasSystem: 'газовая',
  fireAlarmSystem: 'сигнализация',
  absent: 'нет',
  hydrant: 'гидрантная',
  sprinkler: 'спринклерная',
  powder: 'порошковая',
  gas: 'газовая',
  alarm: 'сигнализация',
  no: 'нет',
};

/** `ElectricityReliabilityType`, в шаблоне — `lower` */
export const RELIABILITY_TYPE_LABELS: Partial<Record<string, string>> = {
  first: 'первая',
  second: 'вторая',
  third: 'третья',
};
